/**
 * metrics-shared.tsx — 数据中台各页共享件（移植自原型 metrics-shared.jsx 的 window.MX）
 *
 * 口径徽标 Cal · live/PENDING 数据源标注（LiveTag/PendTag/PendingStat）·
 * 统一顶栏 MetricsToolbar（app_scope 走全局 app-scope-store + AppSwitcher，range 段控）·
 * 四态门 StateGate（loading/empty/error/normal，由页面从真实 query 状态派生——原型的演示 mode
 * 切换器为原型专用，不移植）· 空/错误/PENDING 占位块 · CardHead · fmtUsdMinor。
 * 禁假数据：未就绪指标一律 PENDING 灰显（PENDING_INSTRUMENTATION），不显示例数字。
 */

import type { MetricsRange } from '@novel-admin/shared';
import type { ReactNode } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { Icons } from '~/lib/icons';

// ---------- 金额/数值格式 ----------

/** minor units → $x,xxx.xx；负值（退款冲减日）保留符号；compact 时 ≥$10k 缩写 $x.xk */
export function fmtUsdMinor(
  minor: number | null | undefined,
  opts: { compact?: boolean } = {},
): string {
  if (minor == null) return '—';
  const v = minor / 100;
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (opts.compact && abs >= 10000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------- 口径徽标 ----------

/** 统一小型口径 tag：USD·运营口径 / T+1 / 渠道后台口径 …（§6 指标口径字典 · 单一权威） */
export function Cal({ t, title }: { t: string; title?: string }) {
  return (
    <span
      className="mono"
      title={title || `口径徽标：${t}（指标口径字典 · 单一权威）`}
      style={{
        fontSize: 9.5,
        lineHeight: '14px',
        padding: '0 5px',
        border: '1px solid var(--border)',
        background: 'var(--bg-subtle)',
        color: 'var(--fg-muted)',
        borderRadius: 3,
        whiteSpace: 'nowrap',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    >
      {t}
    </span>
  );
}

// ---------- live / PENDING 数据源标注 ----------
// live 标注由页面按各自真实源传 label/title；未就绪指标一律 PENDING_INSTRUMENTATION 灰显（禁假数据）

export const PENDING_REASON: Record<string, string> = {
  rev: '收入覆盖或对应完整收入版本尚未就绪，核验完成后自动更新',
  beh: '行为类指标待自建埋点 sink 就绪（app_open / chapter_view / reading_sessions / screen_view）',
};

export function PendTag({
  k = 'rev',
  label,
  style,
}: {
  k?: 'rev' | 'beh';
  label?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      className="mono"
      title={`PENDING_INSTRUMENTATION · ${PENDING_REASON[k] || PENDING_REASON.rev}`}
      style={{
        fontSize: 9.5,
        lineHeight: '14px',
        padding: '0 5px',
        border: '1px dashed var(--border-strong)',
        background: 'var(--bg-subtle)',
        color: 'var(--fg-faint)',
        borderRadius: 3,
        whiteSpace: 'nowrap',
        display: 'inline-block',
        verticalAlign: 'middle',
        ...style,
      }}
    >
      {label || (k === 'beh' ? 'PENDING·埋点' : 'PENDING·收入')}
    </span>
  );
}

export function LiveTag({
  label,
  title,
  style,
}: {
  label: string;
  title?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      className="mono"
      title={title || `真实数据源 · ${label}`}
      style={{
        fontSize: 9.5,
        lineHeight: '14px',
        padding: '0 5px 0 4px',
        border: '1px solid color-mix(in oklab, var(--pos) 35%, var(--border))',
        background: 'var(--pos-soft)',
        color: 'var(--pos)',
        borderRadius: 3,
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        verticalAlign: 'middle',
        ...style,
      }}
    >
      <span
        style={{ width: 5, height: 5, borderRadius: 999, background: 'var(--pos)', flexShrink: 0 }}
      />
      {label}
    </span>
  );
}

/** StatCard 形态的 PENDING 占位卡（同网格等高 · 无任何示例数字） */
export function PendingStat({
  label,
  cal,
  k = 'rev',
  note,
}: {
  label: string;
  cal?: string;
  k?: 'rev' | 'beh';
  note?: string;
}) {
  return (
    <div
      className="card"
      title={PENDING_REASON[k]}
      style={{
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
        borderStyle: 'dashed',
        background: 'var(--bg-subtle)',
      }}
    >
      <div className="stat-label hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
        {label} {cal && <Cal t={cal} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          className="mono"
          style={{
            fontSize: 22,
            fontWeight: 500,
            color: 'var(--fg-faint)',
            letterSpacing: '-0.01em',
          }}
        >
          —
        </span>
        <PendTag k={k} />
      </div>
      <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)' }}>
        PENDING_INSTRUMENTATION
      </div>
      {note && (
        <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', lineHeight: 1.5 }}>{note}</div>
      )}
    </div>
  );
}

// ---------- 统一顶栏 ----------

export const METRICS_RANGES_UI: MetricsRange[] = ['24h', '7d', '30d', '90d'];

/** 请求 AppShell 打开全局 AppSwitcher（等价 ⌘K；AppShell 监听本事件） */
export const OPEN_APP_SWITCHER_EVENT = 'na:open-app-switcher';

/**
 * 数据中台统一顶栏：全部 App / 单 App 切换（走全局 app-scope-store + AppSwitcher，不自造 dropdown）
 * + 时间窗段控 + 数据新鲜度。原型的「演示状态切换」不移植（impl 状态由真实 query 派生）。
 */
export function MetricsToolbar({
  appName,
  range,
  setRange,
  freshness,
  right,
}: {
  /** 当前单 App 名（appId!=='all' 时展示；'all' 时按钮显示「单 App…」） */
  appName?: string;
  range: MetricsRange;
  setRange: (r: MetricsRange) => void;
  /** 数据新鲜度标注（如「数据更新至 2026-07-11 · T+1」；缺省不显示） */
  freshness?: string;
  right?: ReactNode;
}) {
  const appId = useAppScope((s) => s.appId);
  const setAppId = useAppScope((s) => s.setAppId);
  const isAll = appId === 'all';
  return (
    <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
      <div className="seg" title="数据范围：全部 App 聚合 / 单 App（复用全局 App 选择器）">
        <button type="button" className={isAll ? 'on' : ''} onClick={() => setAppId('all')}>
          全部 App
        </button>
        <button
          type="button"
          className={!isAll ? 'on' : ''}
          onClick={() => window.dispatchEvent(new Event(OPEN_APP_SWITCHER_EVENT))}
        >
          {isAll ? '单 App…' : appName || '单 App'}
        </button>
      </div>
      <div className="seg">
        {METRICS_RANGES_UI.map((r) => (
          <button
            key={r}
            type="button"
            className={range === r ? 'on' : ''}
            onClick={() => setRange(r)}
          >
            {r}
          </button>
        ))}
      </div>
      {freshness && (
        <span
          className="mono"
          style={{ fontSize: 10.5, color: 'var(--fg-faint)', padding: '0 4px' }}
          title="nightly Cron T+1 聚合 · KV 缓存 60s"
        >
          {freshness}
        </span>
      )}
      <div style={{ flex: 1 }} />
      {right}
    </div>
  );
}

// ---------- 空 / 错误 / PENDING 占位 ----------

export function EmptyBlock({
  title = '暂无数据',
  note = '当前筛选范围内没有可展示的数据',
  minHeight = 260,
  action,
}: {
  title?: string;
  note?: string;
  minHeight?: number;
  action?: ReactNode;
}) {
  return (
    <div
      className="card"
      style={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: 'var(--bg-subtle)',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fg-faint)',
        }}
      >
        <Icons.IconFolder size={16} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--fg-muted)',
          textAlign: 'center',
          maxWidth: 340,
          lineHeight: 1.5,
        }}
      >
        {note}
      </div>
      {action}
    </div>
  );
}

export function ErrorBlock({
  title = '数据加载失败',
  note,
  requestId,
  minHeight = 260,
  onRetry,
}: {
  title?: string;
  note?: string;
  /** 真实请求 ID（来自 AdminApiError.requestId）；无值不渲染 mono 行 */
  requestId?: string;
  minHeight?: number;
  onRetry?: () => void;
}) {
  // 有 requestId 时默认文案说明已上报，便于运营拿 ID 对账服务端日志
  const noteText =
    note ?? (requestId ? '指标服务返回异常 · 已上报 requestId' : '指标服务返回异常 · 请重试');
  return (
    <div
      className="card"
      style={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 24,
        borderColor: 'color-mix(in oklab, var(--neg) 30%, var(--border))',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: 'var(--neg-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--neg)',
        }}
      >
        <Icons.IconAlert size={16} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--fg-muted)',
          textAlign: 'center',
          maxWidth: 360,
          lineHeight: 1.5,
        }}
      >
        {noteText}
      </div>
      {requestId && (
        <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
          {requestId}
        </div>
      )}
      {onRetry && (
        <button type="button" className="btn btn-sm" style={{ marginTop: 4 }} onClick={onRetry}>
          <Icons.IconRefresh size={12} />
          重试
        </button>
      )}
    </div>
  );
}

/** PENDING_INSTRUMENTATION 灰显占位（虚线边 + schematic 底纹 + mono 标签） */
export function PendingBlock({
  title = '埋点数据接入中',
  note,
  height = 200,
  icon = 'IconLayers',
  eta,
  schematic,
}: {
  title?: string;
  note?: string;
  height?: number;
  icon?: keyof typeof Icons;
  eta?: string;
  schematic?: boolean;
}) {
  const Ic = Icons[icon] || Icons.IconLayers;
  return (
    <div
      style={{
        height,
        borderRadius: 6,
        border: '1px dashed var(--border)',
        background: 'var(--bg-subtle)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {schematic && (
        <svg
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, opacity: 0.5 }}
          viewBox="0 0 400 160"
          role="presentation"
        >
          {[0.25, 0.5, 0.75].map((p) => (
            <line
              key={p}
              x1="24"
              x2="392"
              y1={160 * p}
              y2={160 * p}
              stroke="var(--border)"
              strokeDasharray="3 4"
            />
          ))}
          <path
            d="M 24 40 C 90 60, 130 100, 190 112 S 330 132, 392 138"
            fill="none"
            stroke="var(--border-strong)"
            strokeWidth="1.5"
            strokeDasharray="5 5"
          />
          <path
            d="M 24 62 C 90 84, 140 116, 200 126 S 330 142, 392 146"
            fill="none"
            stroke="var(--border)"
            strokeWidth="1.5"
            strokeDasharray="5 5"
          />
        </svg>
      )}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Ic size={20} style={{ color: 'var(--fg-faint)' }} />
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{title}</div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
          PENDING_INSTRUMENTATION
        </div>
        {note && (
          <div
            style={{
              fontSize: 10.5,
              color: 'var(--fg-faint)',
              textAlign: 'center',
              lineHeight: 1.5,
              maxWidth: 300,
            }}
          >
            {note}
          </div>
        )}
        {eta && (
          <span className="chip" style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
            <Icons.IconClock size={10} style={{ marginRight: 3 }} />
            {eta}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------- 四态门 ----------

export type StateGateMode = 'loading' | 'empty' | 'error' | 'normal';

/** 四态门：loading → 骨架；empty/error → 占位块；normal → children。mode 由页面从真实 query 状态派生。 */
export function StateGate({
  mode,
  skeleton,
  emptyTitle,
  emptyNote,
  emptyAction,
  errorNote,
  errorRequestId,
  onRetry,
  children,
}: {
  mode: StateGateMode;
  skeleton?: ReactNode;
  emptyTitle?: string;
  emptyNote?: string;
  emptyAction?: ReactNode;
  errorNote?: string;
  /** 真实请求 ID 透传给 ErrorBlock（页面从 AdminApiError.requestId 取） */
  errorRequestId?: string;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (mode === 'loading') return <>{skeleton}</>;
  if (mode === 'empty')
    return <EmptyBlock title={emptyTitle} note={emptyNote} action={emptyAction} />;
  if (mode === 'error')
    return <ErrorBlock note={errorNote} requestId={errorRequestId} onRetry={onRetry} />;
  return <>{children}</>;
}

// ---------- 卡片头 ----------

export function CardHead({
  title,
  sub,
  tags,
  action,
  mb = 10,
}: {
  title: string;
  sub?: string;
  tags?: string[];
  action?: ReactNode;
  mb?: number;
}) {
  return (
    <div
      className="hstack"
      style={{ justifyContent: 'space-between', marginBottom: mb, alignItems: 'flex-start' }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, fontSize: 13.5 }}>{title}</span>
          {tags?.map((t) => (
            <Cal key={t} t={t} />
          ))}
        </div>
        {sub && <div style={{ color: 'var(--fg-muted)', fontSize: 11.5, marginTop: 2 }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}
